import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { BellRing, Plus, X } from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { API, auth, err } from '../invoices/invoiceApi';

const LABELS = { leads: 'Leads', consultations: 'Consultations', orders: 'Orders', inventory: 'Inventory', reports: 'Reports & digests' };

const ListEditor = ({ t, list, onChange }) => {
  const [draft, setDraft] = useState('');
  const add = () => {
    const e = draft.trim().toLowerCase();
    if (!/^[^@\s]+@[^@\s]+\.[a-z]{2,}$/i.test(e)) { toast.error('Enter a valid email address'); return; }
    if (!list.includes(e)) onChange([...list, e]);
    setDraft('');
  };
  return (
    <div className="bg-white border rounded-lg p-4 space-y-2" data-testid={`notify-type-${t.key}`}>
      <p className="font-semibold text-sm">{LABELS[t.key] || t.key}</p>
      <p className="text-xs text-neutral-500">{t.description}</p>
      {list.length === 0 && <p className="text-xs text-amber-700" data-testid={`notify-empty-${t.key}`}>No recipients: these alerts will not be sent.</p>}
      <div className="flex flex-wrap gap-2">
        {list.map((e) => (
          <span key={e} className="inline-flex items-center gap-1 text-xs bg-neutral-100 rounded-full px-2 py-1" data-testid={`notify-${t.key}-${e}`}>
            {e}<button type="button" onClick={() => onChange(list.filter((x) => x !== e))} data-testid={`notify-remove-${t.key}-${e}`}><X className="w-3 h-3" /></button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <Input value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), add())} placeholder="name@example.com" className="h-8 text-sm" data-testid={`notify-input-${t.key}`} />
        <Button size="sm" variant="outline" onClick={add} data-testid={`notify-add-${t.key}`}><Plus className="w-4 h-4" /></Button>
      </div>
    </div>
  );
};

export default function NotificationRecipientsTab({ token }) {
  const [types, setTypes] = useState(null);
  const [lists, setLists] = useState({});
  const load = (r) => { setTypes(r.data.types); setLists(Object.fromEntries(r.data.types.map((t) => [t.key, t.recipients]))); };
  useEffect(() => { axios.get(`${API}/admin/notification-recipients`, auth(token)).then(load).catch((e) => toast.error(err(e, 'Could not load recipients'))); }, [token]);
  if (!types) return <p className="text-sm text-neutral-500" data-testid="notify-loading">Loading…</p>;
  const save = () => axios.put(`${API}/admin/notification-recipients`, { lists }, auth(token)).then((r) => { load(r); toast.success('Recipients saved'); }).catch((e) => toast.error(err(e, 'Save failed')));
  return (
    <div className="space-y-4 max-w-4xl" data-testid="notify-recipients-tab">
      <h2 className="text-lg font-semibold flex items-center gap-2"><BellRing className="w-5 h-5" />Notification Recipients</h2>
      <p className="text-sm text-neutral-600">Who receives each type of admin alert. Each address gets its own copy.</p>
      <div className="grid md:grid-cols-2 gap-4">
        {types.map((t) => <ListEditor key={t.key} t={t} list={lists[t.key] || []} onChange={(l) => setLists({ ...lists, [t.key]: l })} />)}
      </div>
      <Button onClick={save} data-testid="notify-save">Save Recipients</Button>
    </div>
  );
}
